import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertManager } from '@creditflowresearch/uilib/Alert';
import { getContainer } from '@framework/di/getContainer';
import Logger from '@framework/sentry/Logger';
import BondScreenersRepository from '@/creditflowresearch/infrastructure/repository/BondScreenersRepository';
import { MIN_QUERY_LENGTH } from '@/creditflowresearch/application/BondScreener/constants';
import type { IFilterContext } from '@/creditflowresearch/application/BondScreener/createUseMultiSelectFilter';
import type { IBondScreenerCompany, IBondScreenerTickerOption } from '@/creditflowresearch/domain/vo/Bondscreener';
import useExcludeToggle from './useExcludeToggle';

export type TTickerFilters = {
  includedTickerCompanyIds: string[],
  excludedTickerCompanyIds: string[],
  selectedTickerOptions?: IBondScreenerTickerOption[],
  isTickerExcluded?: boolean,
};

type TTickerIdsKey = 'includedTickerCompanyIds' | 'excludedTickerCompanyIds';

const toTickerOption = ({ id, name, ticker }: IBondScreenerCompany): IBondScreenerTickerOption => ({
  value: id,
  label: ticker ? `${ name } (${ ticker })` : name,
  id,
});

const mergeOptions = (
  primary: IBondScreenerTickerOption[],
  extra: IBondScreenerTickerOption[],
): IBondScreenerTickerOption[] => [
  ...primary,
  ...extra.filter(({ id }) => !primary.some((option) => option.id === id)),
];

const useTickerFilter = (useFilterContext: () => IFilterContext<TTickerFilters>) => {
  const { localFilters, setLocalFilters } = useFilterContext();
  const repository = getContainer().get<BondScreenersRepository>(BondScreenersRepository);
  const { isExcluded, onToggleExclude } = useExcludeToggle({
    includeKey: 'includedTickerCompanyIds',
    excludeKey: 'excludedTickerCompanyIds',
    stateKey: 'isTickerExcluded',
    useFilterContext,
  });

  const idsKey: TTickerIdsKey = isExcluded ? 'excludedTickerCompanyIds' : 'includedTickerCompanyIds';

  const [selectedOptions, setSelectedOptions] = useState<IBondScreenerTickerOption[]>(
    () => (localFilters.selectedTickerOptions ?? []).filter(({ id }) => localFilters[idsKey].includes(id)),
  );
  const latestSelection = useRef(selectedOptions);
  const [currentOptions, setCurrentOptions] = useState<IBondScreenerTickerOption[]>([]);

  const handleChange = useCallback((selected: IBondScreenerTickerOption[]): void => {
    latestSelection.current = selected;
    setSelectedOptions(selected);
    setLocalFilters((prev) => ({
      ...prev,
      selectedTickerOptions: selected,
      [idsKey]: selected.map(({ id }) => id),
    }));
  }, [setLocalFilters, idsKey]);

  useEffect(() => {
    const ids = localFilters[idsKey];
    if (selectedOptions.length === ids.length) {
      return undefined;
    }

    let isMounted = true;

    const lookUpOptions = async (): Promise<IBondScreenerTickerOption[]> => {
      try {
        const { issuers } = await repository.getCompanyList({ includedCompanyIds: ids });

        return issuers.filter(({ id }) => ids.includes(id)).map(toTickerOption);
      } catch (error) {
        Logger.error({ message: 'Failed to resolve initial company names for Ticker filter', error });

        return ids.map((id) => toTickerOption({ id, name: id }));
      }
    };

    const resolveInitialOptions = async (): Promise<void> => {
      const found = await lookUpOptions();
      if (isMounted) {
        handleChange(mergeOptions(found, latestSelection.current));
      }
    };

    resolveInitialOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const loadOptions = useCallback(async (
    query: string,
    callback: (options: IBondScreenerTickerOption[]) => void,
  ): Promise<IBondScreenerTickerOption[]> => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length < MIN_QUERY_LENGTH.ticker) {
      callback([]);
      return [];
    }
    setCurrentOptions([]);
    try {
      const { issuers } = await repository.getCompanyList({ searchByTicker: trimmedQuery });
      const options = issuers.map(toTickerOption);
      setCurrentOptions(options);
      callback(options);
      return options;
    } catch (error) {
      Logger.error({ message: 'Failed to load ticker company list', error });
      AlertManager.error('Failed to load tickers');
      callback([]);
      return [];
    }
  }, [repository]);

  return { loadOptions, selectedOptions, handleChange, currentOptions, isExcluded, onToggleExclude };
};

export default useTickerFilter;
